/**
 * Developer settings store.
 * Only available in development environment.
 * Manages developer-only settings (both visible and hidden).
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { DEFAULT_CAMERA_CONFIG, CameraConfig } from '../config/settings/camera';
import { DEFAULT_BUILDING_CONFIG, BuildingConfig } from '../config/settings/buildings';
import { getDefaultUnitConfig, UnitConfig } from '../config/settings/units';

/**
 * Bounds for player-editable camera settings.
 * Developers can set min/max bounds that constrain what players can adjust.
 */
export interface CameraSettingBounds {
  panSpeed: { min: number; max: number };
  edgeScrollSpeed: { min: number; max: number };
}

/**
 * Map selection options.
 */
export type MapSelection = 'static-map' | 'test-map' | 'procedural';

/**
 * Developer settings interface.
 * Includes all settings with DEVELOPER or DEVELOPER_HIDDEN visibility.
 */
export interface DevSettings {
  camera: Pick<
    CameraConfig,
    'enableEdgeScrolling' | 'enableKeyboardPanning' | 'minZoom' | 'maxZoom'
  >;
  cameraBounds: CameraSettingBounds;
  buildings: BuildingConfig;
  units: UnitConfig;
  mapSelection: MapSelection;
  // Future: debug flags, performance overrides, etc.
}

/**
 * Default bounds for player settings.
 */
const DEFAULT_CAMERA_BOUNDS: CameraSettingBounds = {
  panSpeed: { min: 1, max: 50 },
  edgeScrollSpeed: { min: 1, max: 50 },
};

/**
 * Get default developer settings.
 */
function getDefaultDevSettings(): DevSettings {
  return {
    camera: {
      enableEdgeScrolling: DEFAULT_CAMERA_CONFIG.enableEdgeScrolling,
      enableKeyboardPanning: DEFAULT_CAMERA_CONFIG.enableKeyboardPanning,
      minZoom: DEFAULT_CAMERA_CONFIG.minZoom,
      maxZoom: DEFAULT_CAMERA_CONFIG.maxZoom,
    },
    cameraBounds: DEFAULT_CAMERA_BOUNDS,
    buildings: { ...DEFAULT_BUILDING_CONFIG },
    units: { ...getDefaultUnitConfig() },
    mapSelection: 'procedural', // Default to procedural generation
  };
}

interface DevSettingsState {
  settings: DevSettings;
  updateCameraSettings: (settings: Partial<DevSettings['camera']>) => void;
  updateCameraBounds: (bounds: Partial<DevSettings['cameraBounds']>) => void;
  updateBuildingSettings: (settings: Partial<DevSettings['buildings']>) => void;
  updateUnitSettings: (settings: Partial<DevSettings['units']>) => void;
  updateMapSelection: (selection: MapSelection) => void;
  resetSettings: () => void;
  resetCameraSettings: () => void;
  resetCameraBounds: () => void;
  resetBuildingSettings: () => void;
  resetUnitSettings: () => void;
}

/**
 * Merge persisted state with defaults to ensure all fields are present.
 * This is important because Zustand's persist does a shallow merge by default,
 * so nested objects like `units` need to be deep-merged with defaults.
 */
function mergePersistedState(
  persistedState: unknown,
  currentState: DevSettingsState
): DevSettingsState {
  // Type guard to check if persistedState has the expected structure
  if (!persistedState || typeof persistedState !== 'object' || !('settings' in persistedState)) {
    return currentState;
  }

  const persisted = persistedState as { settings?: Partial<DevSettings> };
  if (!persisted.settings) {
    return currentState;
  }

  const defaults = getDefaultDevSettings();
  const persistedSettings = persisted.settings;

  return {
    ...currentState,
    settings: {
      camera: {
        ...defaults.camera,
        ...(persistedSettings.camera || {}),
      },
      cameraBounds: {
        ...defaults.cameraBounds,
        ...(persistedSettings.cameraBounds || {}),
      },
      buildings: {
        ...defaults.buildings,
        ...(persistedSettings.buildings || {}),
      },
      units: {
        ...defaults.units,
        ...(persistedSettings.units || {}),
      },
      mapSelection: persistedSettings.mapSelection ?? defaults.mapSelection,
    },
  };
}

/**
 * Developer settings store with localStorage persistence.
 * Only persists in development environment.
 */
// const isDevelopment = import.meta.env.DEV;

export const useDevSettingsStore = create<DevSettingsState>()(
  persist(
    (set) => ({
      settings: getDefaultDevSettings(),
      updateCameraSettings: (updates) =>
        set((state) => ({
          settings: {
            ...state.settings,
            camera: { ...state.settings.camera, ...updates },
          },
        })),
      updateCameraBounds: (updates) =>
        set((state) => ({
          settings: {
            ...state.settings,
            cameraBounds: {
              ...(state.settings.cameraBounds ?? DEFAULT_CAMERA_BOUNDS),
              ...updates,
            },
          },
        })),
      resetSettings: () => set({ settings: getDefaultDevSettings() }),
      resetCameraSettings: () =>
        set((state) => ({
          settings: {
            ...state.settings,
            camera: {
              enableEdgeScrolling: DEFAULT_CAMERA_CONFIG.enableEdgeScrolling,
              enableKeyboardPanning: DEFAULT_CAMERA_CONFIG.enableKeyboardPanning,
              minZoom: DEFAULT_CAMERA_CONFIG.minZoom,
              maxZoom: DEFAULT_CAMERA_CONFIG.maxZoom,
            },
            cameraBounds: DEFAULT_CAMERA_BOUNDS,
          },
        })),
      resetCameraBounds: () =>
        set((state) => ({
          settings: {
            ...state.settings,
            cameraBounds: DEFAULT_CAMERA_BOUNDS,
          },
        })),
      updateBuildingSettings: (updates) =>
        set((state) => ({
          settings: {
            ...state.settings,
            buildings: { ...state.settings.buildings, ...updates },
          },
        })),
      resetBuildingSettings: () =>
        set((state) => ({
          settings: {
            ...state.settings,
            buildings: { ...DEFAULT_BUILDING_CONFIG },
          },
        })),
      updateUnitSettings: (updates) =>
        set((state) => ({
          settings: {
            ...state.settings,
            units: { ...state.settings.units, ...updates },
          },
        })),
      resetUnitSettings: () =>
        set((state) => ({
          settings: {
            ...state.settings,
            units: { ...getDefaultUnitConfig() },
          },
        })),
      updateMapSelection: (selection) =>
        set((state) => ({
          settings: {
            ...state.settings,
            mapSelection: selection,
          },
        })),
    }),
    {
      name: 'castle-survivors-dev-settings',
      storage: createJSONStorage(() => localStorage),
      skipHydration: false,
      merge: mergePersistedState,
    }
  )
);

/**
 * Check if dev settings are available.
 * In production, this always returns false.
 */
export function isDevSettingsAvailable(): boolean {
  // return isDevelopment;
  return true;
}
